import React, { useState, useEffect } from 'react';
import { getBlogs, searchBlogs, formatBlogDate, truncateText } from '../api/blogApi';
import BlogCard from '../components/BlogCard/BlogCard';
import BlogDashboard from '../components/BlogManagement/BlogDashboard';
import BlogGenerationForm from '../components/BlogGeneration/BlogGenerationForm';
import Navbar from '../components/Navbar/Navbar';
import PageTitle from '../components/PageTitle/PageTitle';
import Footer from '../sections/Footer/Footer';
import './BlogsPage.scss';

const BlogsPage = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('public'); // 'public' or 'admin'
    const [searchQuery, setSearchQuery] = useState('');
    const [showGenerationForm, setShowGenerationForm] = useState(false);
    const [clinicId, setClinicId] = useState(null); // This would come from user context

    useEffect(() => {
        fetchBlogs();
        // In a real app, you'd get clinicId from user authentication context
        setClinicId(1); // Temporary for demo
    }, []);

    const fetchBlogs = async () => {
        try {
            const data = await getBlogs({ status: 'published' });
            setBlogs(data.results || data);
        } catch (error) {
            console.error('Error fetching blogs:', error);
            setBlogs([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async () => {
        if (!searchQuery.trim()) {
            fetchBlogs();
            return;
        }

        setLoading(true);
        try {
            const data = await searchBlogs(searchQuery, { status: 'published' });
            setBlogs(data.results || data);
        } catch (error) {
            console.error('Error searching blogs:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleBlogGenerated = (blogPost) => {
        setShowGenerationForm(false);
        // Refresh the blogs list
        fetchBlogs();
    };

    const renderPublicView = () => (
        <>
            <div className="blogs-header">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-md-8">
                            <h1>📚 Dental Health Blog</h1>
                            <p>Expert insights, tips, and information about dental care and oral health</p>
                        </div>
                        <div className="col-md-4 text-end">
                            <button 
                                className="btn btn-outline-primary"
                                onClick={() => setViewMode('admin')}
                            >
                                <i className="fas fa-cog"></i> Admin Panel
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="search-section">
                <div className="container">
                    <div className="search-container">
                        <input
                            type="text"
                            placeholder="Search dental topics, treatments, or health tips..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        />
                        <button className="btn btn-primary" onClick={handleSearch}>
                            <i className="fas fa-search"></i> Search
                        </button>
                    </div>
                </div>
            </div>

            <section className='blogs-content pb-70' data-aos="fade-up" data-aos-duration="2000">
                <div className="container">
                    {loading ? (
                        <div className="loading-spinner">
                            <i className="fas fa-spinner fa-spin"></i>
                            <p>Loading dental health articles...</p>
                        </div>
                    ) : blogs.length === 0 ? (
                        <div className="no-blogs">
                            <i className="fas fa-file-alt"></i>
                            <h3>No articles found</h3>
                            <p>Check back soon for new dental health content!</p>
                        </div>
                    ) : (
                        <div className="row">
                            {blogs.map(blog => (
                                <BlogCard key={blog.id} blog={blog} />
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </>
    );

    const renderAdminView = () => (
        <div className="admin-view">
            <div className="admin-header">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-md-8">
                            <h2>🤖 AI Blog Management</h2>
                            <p>Generate, manage, and optimize your dental clinic's blog content</p>
                        </div>
                        <div className="col-md-4 text-end">
                            <button 
                                className="btn btn-outline-secondary"
                                onClick={() => setViewMode('public')}
                            >
                                <i className="fas fa-eye"></i> Public View
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {showGenerationForm ? (
                <div className="container">
                    <div className="generation-form-container">
                        <button 
                            className="btn btn-outline-secondary mb-3"
                            onClick={() => setShowGenerationForm(false)}
                        >
                            <i className="fas fa-arrow-left"></i> Back to Dashboard
                        </button>
                        <BlogGenerationForm 
                            onBlogGenerated={handleBlogGenerated}
                            clinicId={clinicId}
                        />
                    </div>
                </div>
            ) : (
                <BlogDashboard clinicId={clinicId} />
            )}
        </div>
    );

    return (
        <>
            <Navbar />
            <PageTitle title="Dental Health Blog" />
            
            {viewMode === 'public' ? renderPublicView() : renderAdminView()}
            
            <Footer />
        </>
    );
};

export default BlogsPage;